import { faReceipt, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { AccountLocationPicker } from '../components/AccountLocationPicker';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { Paginator } from '../components/Paginator';
import { TransactionRow } from '../components/TransactionRow';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalTransactionService from '../services/portalTransactionService';
import { useLoginModalStore } from '../stores/loginModalStore';

const PAGE_SIZE = 20;

/**
 * Transaction history (PD-14080) — payments, captures, voids, refunds across
 * the customer's assigned clients, modeled on the invoices page and scoped
 * server-side exactly like the CRM transactions list is for the client.
 */
export function TransactionsPage(): JSX.Element {
	const { t } = useTranslation();
	const { token, isAuthenticated, availableClients } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [searchParams, setSearchParams] = useSearchParams();

	const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
	const clientIdParam = searchParams.get('clientId') ?? '';

	const { data, isLoading } = useQuery({
		queryKey: ['portal-transactions', currentPage, clientIdParam],
		queryFn: () =>
			portalTransactionService.getTransactions(token ?? '', {
				page: currentPage,
				limit: PAGE_SIZE,
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
			}),
		enabled: token != null,
		staleTime: 30_000,
	});

	const transactions = data?.data ?? [];
	const pagination = data?.pagination ?? null;

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateParams({ page: page > 1 ? String(page) : null });
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},
		[updateParams],
	);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Transactions" backPath="/account" />
			<AccountBreadcrumb current="Transactions" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
				{t('Transactions')}
			</h1>

			{/* Client/location filter */}
			{availableClients.length > 1 && (
				<div className="mb-4">
					<AccountLocationPicker
						value={clientIdParam}
						onChange={(value) => {
							updateParams({
								clientId: value.length > 0 ? value : null,
								page: null,
							});
						}}
						clients={availableClients}
					/>
				</div>
			)}

			{isLoading ? (
				<div className="flex items-center justify-center py-16 text-brand-muted">
					<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
					{t('Loading...')}
				</div>
			) : transactions.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-16 text-brand-muted">
					<FontAwesomeIcon icon={faReceipt} className="text-3xl mb-3" />
					{t('No transactions yet')}
				</div>
			) : (
				<div className="flex flex-col gap-2">
					{transactions.map((txn) => (
						<TransactionRow
							key={txn.id}
							txn={txn}
							showClient={availableClients.length > 1}
						/>
					))}
				</div>
			)}

			{pagination != null && pagination.totalPages > 1 && (
				<div className="mt-4">
					<Paginator
						currentPage={pagination.page}
						totalPages={pagination.totalPages}
						onPageChange={handlePageChange}
					/>
				</div>
			)}
		</div>
	);
}
